import PropTypes from 'prop-types';
import { Box, Card, Chip, Tooltip, Typography } from '@material-ui/core';
import GroupsIcon from '@mui/icons-material/GroupsRounded';
import EventIcon from '@mui/icons-material/EventRounded';
import LayersIcon from '@mui/icons-material/LayersRounded';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardRounded';
import StarRating from './StarRating';
import { ScoreRing } from './SessionResponseCard';
import { formatDateRange, formatStyle, scoreColor, scoreLabel } from './constants';
import { alpha } from '@mui/material/styles';
import { grey } from '../../../../theme/tones';

const MetaLine = ({ icon, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
    <Box sx={{ color: 'text.disabled', display: 'flex', fontSize: 15 }}>{icon}</Box>
    <Typography noWrap sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 600 }}>
      {children}
    </Typography>
  </Box>
);

MetaLine.propTypes = { icon: PropTypes.node, children: PropTypes.node };

/**
 * Une carte = une formation, c'est-à-dire l'agrégat de toutes ses sessions
 * ayant reçu des réponses. Le clic ne charge rien : il déplie la liste des
 * sessions de la formation, où le détail des réponses reste à un clic de plus.
 */
const FormationResponseCard = ({ formation, accent, showRating, showScore, onOpen }) => {
  const color = scoreColor(formation.average);

  return (
    <Card
      onClick={() => onOpen(formation)}
      sx={{
        p: 2.25,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.75,
        borderRadius: '20px',
        border: `1px solid ${grey(100)}`,
        boxShadow: (theme) => `0 1px 3px ${alpha(theme.palette.common.black, 0.05)}`,
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform .18s ease, box-shadow .18s ease, border-color .18s ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          borderColor: accent,
          boxShadow: `0 12px 28px -12px ${accent}66`
        },
        '&:hover .card-cta': { opacity: 1, transform: 'translateX(0)' }
      }}
    >
      {/* Liseré de score sur la tranche gauche — teinté par l'accent quand la
          carte ne porte pas de score (comptes rendus : la moyenne mélangerait
          des types de questionnaires qui ne se comparent pas). */}
      <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: showScore ? color : accent }} />

      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center', pl: 0.5 }}>
        {formation.formats.slice(0, 2).map((f) => {
          const fmt = formatStyle(f);
          return (
            <Chip
              key={f}
              label={f}
              size="small"
              sx={{ bgcolor: fmt.bg, color: fmt.color, fontWeight: 800, fontSize: '0.65rem', height: 21 }}
            />
          );
        })}
        {formation.formats.length > 2 && (
          <Tooltip title={formation.formats.slice(2).join(', ')}>
            <Chip
              label={`+${formation.formats.length - 2}`}
              size="small"
              sx={{ bgcolor: (theme) => alpha(theme.palette.text.secondary, 0.12), color: 'text.secondary', fontWeight: 800, fontSize: '0.65rem', height: 21 }}
            />
          </Tooltip>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 900, color: 'text.disabled', letterSpacing: '0.04em' }}>
          FORMATION
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, pl: 0.5 }}>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Tooltip title={formation.formation_name || ''}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '0.92rem',
                color: 'text.primary',
                lineHeight: 1.35,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              {formation.formation_name || 'Formation inconnue'}
            </Typography>
          </Tooltip>
          {formation.program_number && (
            <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', fontWeight: 700, mt: 0.5 }}>
              N° {formation.program_number}
            </Typography>
          )}
        </Box>
        {showScore && (
          <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.4 }}>
            <ScoreRing value={formation.average} />
            {showRating && <StarRating percent={formation.average} size="0.74rem" color={color} iconSize="0.92rem" />}
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 900, color, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {scoreLabel(formation.average)}
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 0.5 }}>
        <MetaLine icon={<LayersIcon fontSize="inherit" />}>
          {formation.sessions} session{formation.sessions > 1 ? 's' : ''} avec réponses
        </MetaLine>
        <MetaLine icon={<EventIcon fontSize="inherit" />}>
          {formatDateRange(formation.start_date, formation.end_date)}
        </MetaLine>
      </Box>

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', pl: 0.5 }}>
        {formation.professions.slice(0, 3).map((p) => (
          <Chip
            key={p}
            label={p}
            size="small"
            sx={{ bgcolor: (theme) => alpha(theme.palette.text.secondary, 0.08), color: 'text.secondary', fontWeight: 700, fontSize: '0.62rem', height: 19 }}
          />
        ))}
        {formation.professions.length > 3 && (
          <Tooltip title={formation.professions.slice(3).join(', ')}>
            <Chip
              label={`+${formation.professions.length - 3}`}
              size="small"
              sx={{ bgcolor: (theme) => alpha(theme.palette.text.secondary, 0.08), color: 'text.secondary', fontWeight: 700, fontSize: '0.62rem', height: 19 }}
            />
          </Tooltip>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pt: 1.5, pl: 0.5, borderTop: `1px dashed ${grey(200)}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
          <GroupsIcon sx={{ fontSize: 16, color: accent }} />
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: 'text.primary' }}>
            {formation.responses}
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled', fontWeight: 600 }}>
            {formation.responses > 1 ? 'réponses' : 'réponse'}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '0.72rem', color: grey(300) }}>•</Typography>
        <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled', fontWeight: 600 }}>
          {formation.learners} apprenant{formation.learners > 1 ? 's' : ''}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Box
          className="card-cta"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.4,
            color: accent,
            fontWeight: 800,
            fontSize: '0.72rem',
            opacity: 0,
            transform: 'translateX(-6px)',
            transition: 'opacity .18s ease, transform .18s ease',
            whiteSpace: 'nowrap'
          }}
        >
          Sessions <ArrowForwardIcon sx={{ fontSize: 14 }} />
        </Box>
      </Box>
    </Card>
  );
};

FormationResponseCard.defaultProps = { showScore: true };

FormationResponseCard.propTypes = {
  formation: PropTypes.object.isRequired,
  accent: PropTypes.string.isRequired,
  showRating: PropTypes.bool,
  /** À false, la carte ne montre plus l'anneau de score (moyenne non pertinente). */
  showScore: PropTypes.bool,
  onOpen: PropTypes.func.isRequired
};

export default FormationResponseCard;
